/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
        this.setupSheet();
      }
    }

    /**
     * Mobile/tablet "Titan style" two-pane filter sheet.
     * Left pane = filter categories, right pane = the options of the active category.
     */
    setupSheet() {
      if (!this.filteringEnabled || !this.form) return;

      const navButtons = Array.from(this.querySelectorAll('.js-facet-nav'));
      if (!navButtons.length) return;

      const ids = navButtons.map((btn) => btn.dataset.target);
      if (!this.activeFacet || !ids.includes(this.activeFacet)) [this.activeFacet] = ids;

      navButtons.forEach((btn) => {
        const isActive = btn.dataset.target === this.activeFacet;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.querySelectorAll('.facets__panels > details-disclosure').forEach((wrapper) => {
        const details = wrapper.querySelector('details');
        if (!details) return;
        wrapper.classList.toggle('is-sheet-active', details.id === this.activeFacet);
        // Every category is always "open" inside the sheet; only the active one is visible.
        details.open = true;
      });
    }

    /**
     * Discards filter changes the shopper made but never applied (sheet closed without "Apply").
     */
    resetUnappliedChanges() {
      if (!this.form) return;
      this.form.reset();
      this.form.querySelectorAll('input[type="number"], input[type="range"]').forEach((input) => {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }

    /**
     * Closes the sheet. Unapplied changes are thrown away unless closing after "Apply".
     */
    close(callback) {
      if (!theme.mediaMatches.lg && !this.skipReset) this.resetUnappliedChanges();
      super.close(callback);
    }

    /**
     * Click handling for the sheet footer buttons (the drawer only listens while open).
     */
    handleClick(evt) {
      const applyBtn = evt.target.closest('.js-sheet-apply');
      const clearBtn = evt.target.closest('.js-sheet-clear');

      if (applyBtn) {
        evt.preventDefault();
        this.applySheet();
        return;
      }

      if (clearBtn) {
        evt.preventDefault();
        const url = new URL(clearBtn.dataset.href, window.location.origin);
        this.applyFilters(url.searchParams.toString(), evt);
        return;
      }

      super.handleClick(evt);
    }

    /**
     * "Apply" button: fetch the results for the selected filters, then close the sheet.
     */
    async applySheet() {
      clearTimeout(this.filterChangeTimeout);
      await this.applyFilters(this.buildSearchParams(), null);
      this.skipReset = true;
      this.close();
      this.skipReset = false;
    }

    /**
     * Applies a new sort order while keeping the currently applied filters.
     * @param {string} value - Sort value, e.g. 'price-ascending'.
     */
    applySort(value) {
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', value);
      params.delete('page');
      const sortInput = this.form && this.form.querySelector('input[name="sort_by"]');
      if (sortInput) sortInput.value = value;
      this.applyFilters(params.toString(), null);
    }

    /**
     * Builds the search params string from the current state of the filters form.
     * @param {string} [sortValue] - Optional sort value to force.
     * @returns {string} Search params.
     */
    buildSearchParams(sortValue) {
      const searchParams = new URLSearchParams(new FormData(this.form));

      if (this.sortingEnabled) {
        const currentSortBy = sortValue || searchParams.get('sort_by');
        if (currentSortBy) searchParams.set('sort_by', currentSortBy);
      }

      const emptyParams = [];
      searchParams.forEach((value, key) => {
        if (!value) emptyParams.push(key);
      });
      emptyParams.forEach((key) => searchParams.delete(key));

      return searchParams.toString();
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      const isSortSelect = evt.target.tagName === 'CUSTOM-SELECT';

      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      // Below desktop the filters live in a sheet and are only applied with the "Apply" button.
      if (!isSortSelect && !theme.mediaMatches.lg) return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        // The sort dropdown in the toolbar (tablet/desktop) carries its own value.
        const sortValue = isSortSelect ? evt.detail.selectedValue : null;
        if (sortValue) {
          const sortInput = this.form.querySelector('input[name="sort_by"]');
          if (sortInput) sortInput.value = sortValue;
        }

        this.applyFilters(this.buildSearchParams(sortValue), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter category (left pane of the mobile sheet) clicked.
      const navBtn = target.closest('.js-facet-nav');
      if (navBtn) {
        this.activeFacet = navBtn.dataset.target;
        this.setupSheet();
        const panels = this.querySelector('.facets__panels');
        if (panels) panels.scrollTop = 0;
        return;
      }

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the "(N Products Found)" count in the sheet header and the "Clear all" link
          const newCount = tmpl.content.querySelector('.js-facets-count');
          const currentCount = this.querySelector('.js-facets-count');
          if (newCount && currentCount) currentCount.textContent = newCount.textContent;

          const newClear = tmpl.content.querySelector('.js-sheet-clear');
          const currentClear = this.querySelector('.js-sheet-clear');
          if (newClear && currentClear) currentClear.dataset.href = newClear.dataset.href;

          // Keep the "Sort By" sheet in sync (selected option is bold)
          const newSortList = tmpl.content.querySelector('.sort-sheet__list');
          const currentSortList = document.querySelector('.sort-sheet__list');
          if (newSortList && currentSortList) currentSortList.innerHTML = newSortList.innerHTML;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          if (!theme.mediaMatches.lg) this.setupSheet();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}

/**
 * Titan-style "Sort By" bottom sheet (mobile). Tapping an option applies it and closes the sheet.
 */
if (!customElements.get('sort-sheet')) {
  class SortSheet extends SideDrawer {
    handleClick(evt) {
      const option = evt.target.closest('.js-sort-option');

      if (option) {
        evt.preventDefault();
        this.select(option.dataset.value);
        return;
      }

      if (evt.target.closest('.js-close-drawer') || evt.target === this.overlay) {
        this.close();
      }
    }

    /**
     * @param {string} value - Sort value to apply.
     */
    select(value) {
      const filters = document.querySelector('facet-filters');
      this.close();
      if (filters) filters.applySort(value);
    }
  }

  customElements.define('sort-sheet', SortSheet);
}
